import { Bug, CheckCircle2, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { TabBar } from "@/components/shared/tab-bar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  ERROR_LOG_PAGE_SIZE,
  type ErrorLogPageRequest,
  type ErrorLogRow,
  useErrorLog,
  useSetErrorStatus,
} from "@/hooks/use-error-log";
import { formatExact, formatWhen } from "@/lib/dates";
import { ERROR_SOURCE_LABELS, type ErrorSource, type ErrorView } from "@/schemas/error-log";

const SOURCE_TONE: Record<ErrorSource, StatusTone> = {
  error_boundary: "danger",
  window_error: "warning",
  unhandled_rejection: "info",
};

const VIEW_TABS = [
  { key: "open", label: "Open" },
  { key: "fixed", label: "Fixed" },
];

/**
 * Settings → Error log: frontend errors captured automatically by
 * lib/error-reporter.ts, so they can be looked at before anyone reports them.
 *
 * Two tabs, because a fixed error shouldn't clutter the list you triage from.
 * The split is derived, not stored: an error marked fixed that fires AGAIN
 * comes back here under Open with a "regressed" pill (see api/error-log.ts) —
 * marking something fixed can't bury a live bug.
 *
 * Read-only apart from the fixed/open toggle. There's no delete: the occurrence
 * count is the history, and the rows are kept like audit rows.
 */
export function ErrorLogTab() {
  const [view, setView] = useState<ErrorView>("open");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<ErrorLogPageRequest>({
    cursor: null,
    direction: "next",
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  /** The row whose "what was the fix?" dialog is open. */
  const [fixTarget, setFixTarget] = useState<ErrorLogRow | null>(null);

  const { data, isLoading, isError, error, refetch } = useErrorLog(
    { view, search: debouncedSearch },
    pageRequest,
  );
  const setStatus = useSetErrorStatus();

  /** Any filter/tab change invalidates the cursor — go back to page one. */
  function resetPaging() {
    setPage(1);
    setPageRequest({ cursor: null, direction: "next" });
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the error log"}
        onRetry={() => refetch()}
      />
    );
  }

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold text-foreground">Error log</h2>
        <p className="text-sm text-muted-foreground">
          Frontend errors captured automatically as people use the app — React render crashes,
          uncaught errors, and unhandled promise rejections. Repeats of the same error group into
          one row with a count rather than filling the list.
        </p>
      </div>

      <TabBar
        tabs={VIEW_TABS}
        value={view}
        onChange={(key) => {
          setView(key === "fixed" ? "fixed" : "open");
          setExpandedId(null);
          resetPaging();
        }}
      />

      <SearchInput
        value={search}
        onValueChange={(value) => {
          setSearch(value);
          resetPaging();
        }}
        placeholder="Search error messages…"
      />

      <DataTable
        headers={[
          { label: "Error" },
          { label: "Times", className: "w-20 text-right" },
          { label: "Last seen", className: "w-32" },
          { label: "First seen", className: "hidden lg:table-cell w-32" },
          { label: "", className: "w-32 text-right" },
        ]}
        loading={isLoading}
        colSpan={5}
        empty={
          <EmptyState
            icon={view === "fixed" ? CheckCircle2 : Bug}
            title={view === "fixed" ? "Nothing marked fixed yet" : "No errors captured"}
            description={
              view === "fixed"
                ? "Errors you mark as fixed move here. If one fires again, it goes back to Open with a regressed flag."
                : debouncedSearch
                  ? "No captured error matches that search."
                  : "Nothing has gone wrong in the browser since capture was switched on. Long may it last."
            }
          />
        }
      >
        {rows.map((row) => (
          <ErrorRow
            key={row.id}
            row={row}
            expanded={expandedId === row.id}
            onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)}
            // Marking fixed needs a note, so it goes through the dialog;
            // reopening is a plain one-click action.
            onMarkFixed={() => setFixTarget(row)}
            onReopen={() => setStatus.mutate({ id: row.id, status: "open" })}
            busy={setStatus.isPending}
          />
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={page}
        limit={ERROR_LOG_PAGE_SIZE}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => {
          if (data?.nextCursor) {
            setPage((p) => p + 1);
            setPageRequest({ cursor: data.nextCursor, direction: "next" });
          }
        }}
        onPrev={() => {
          if (data?.prevCursor) {
            setPage((p) => Math.max(1, p - 1));
            setPageRequest({ cursor: data.prevCursor, direction: "prev" });
          }
        }}
        loading={isLoading}
        itemLabel="errors"
      />

      {fixTarget && (
        <FixNoteDialog
          row={fixTarget}
          onClose={() => setFixTarget(null)}
          onSave={(fixNote) =>
            setStatus.mutate(
              { id: fixTarget.id, status: "fixed", fixNote },
              { onSuccess: () => setFixTarget(null) },
            )
          }
          busy={setStatus.isPending}
        />
      )}
    </div>
  );
}

/**
 * Marking an error fixed requires saying what the fix was — six months from now
 * a "fixed" row with no explanation tells you nothing. The server enforces it
 * too; this just makes the requirement obvious before the request goes out.
 *
 * On a re-fix the previous note is shown rather than pre-filled: the new note
 * should describe what was done this time, not inherit a fix that didn't hold.
 */
function FixNoteDialog({
  row,
  onClose,
  onSave,
  busy,
}: {
  row: ErrorLogRow;
  onClose: () => void;
  onSave: (fixNote: string) => void;
  busy: boolean;
}) {
  const [note, setNote] = useState("");
  const canSave = note.trim().length > 0 && !busy;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>What was the fix?</DialogTitle>
          <DialogDescription>
            Recorded against this error so anyone looking later can see what was done. Required.
          </DialogDescription>
        </DialogHeader>

        <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm break-words">{row.message}</p>

        {row.fixNote && (
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold">
              {row.regressed ? "Previous fix (didn't hold):" : "Previous note:"}
            </span>{" "}
            {row.fixNote}
          </div>
        )}

        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={2000}
          autoFocus
          placeholder="e.g. Guarded against the missing person record in PersonProfile — shipped in 0.14.1"
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSave} onClick={() => onSave(note.trim())}>
            {busy ? "Saving…" : "Mark fixed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ErrorRow({
  row,
  expanded,
  onToggle,
  onMarkFixed,
  onReopen,
  busy,
}: {
  row: ErrorLogRow;
  expanded: boolean;
  onToggle: () => void;
  onMarkFixed: () => void;
  onReopen: () => void;
  busy: boolean;
}) {
  const hasDetail = Boolean(row.stack || row.componentStack || row.userAgent);

  return (
    <>
      <tr className="border-b align-top">
        <td className="px-4 py-3">
          <div className="flex items-start gap-2">
            {hasDetail ? (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                aria-label={expanded ? "Hide details" : "Show details"}
                className="mt-0.5 text-muted-foreground transition-colors hover:text-foreground"
              >
                {expanded ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
              </button>
            ) : (
              <span className="size-4 shrink-0" aria-hidden />
            )}
            <div className="min-w-0 space-y-1">
              <div className="break-words font-medium">{row.message}</div>
              <div className="flex flex-wrap items-center gap-1.5">
                <StatusPill
                  tone={SOURCE_TONE[row.source]}
                  label={ERROR_SOURCE_LABELS[row.source]}
                />
                {/* A fix that didn't hold. Loud on purpose — this row was
                    believed done and is demonstrably not. */}
                {row.regressed && <StatusPill tone="danger" label="Regressed" />}
                {row.routePath && (
                  <span className="font-mono text-xs text-muted-foreground">{row.routePath}</span>
                )}
              </div>
              {row.lastUserName && (
                <div className="text-xs text-muted-foreground">
                  Last hit by {row.lastUserName}
                  {row.lastUserEmail ? ` (${row.lastUserEmail})` : ""}
                </div>
              )}
              {/* The reason the note is required: on a fixed row this is the
                  answer to "what did we do about this?". On a regressed one it's
                  the attempt that didn't hold, which is just as useful. */}
              {row.fixNote && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold">
                    {row.status === "fixed" && !row.regressed
                      ? "Fix:"
                      : row.regressed
                        ? "Previous fix (didn't hold):"
                        : "Previous fix:"}
                  </span>{" "}
                  <span className="whitespace-pre-wrap">{row.fixNote}</span>
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-right font-semibold tabular-nums">{row.occurrenceCount}</td>
        <td className="px-4 py-3 text-xs text-muted-foreground" title={formatExact(row.lastSeenAt)}>
          {formatWhen(row.lastSeenAt)}
        </td>
        <td
          className="hidden px-4 py-3 text-xs text-muted-foreground lg:table-cell"
          title={formatExact(row.firstSeenAt)}
        >
          {formatWhen(row.firstSeenAt)}
        </td>
        <td className="px-4 py-3 text-right">
          {row.status === "fixed" && !row.regressed ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onReopen}>
              <RotateCcw className="mr-1.5 size-3.5" />
              Reopen
            </Button>
          ) : (
            <Button variant="outline" size="sm" disabled={busy} onClick={onMarkFixed}>
              <CheckCircle2 className="mr-1.5 size-3.5" />
              {row.regressed ? "Fixed again" : "Mark fixed"}
            </Button>
          )}
        </td>
      </tr>

      {expanded && (
        <tr className="border-b bg-muted/30">
          <td colSpan={5} className="px-4 py-3">
            <div className="space-y-3 text-xs">
              {row.stack && (
                <div>
                  <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
                    Stack
                  </div>
                  <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {row.stack}
                  </pre>
                </div>
              )}
              {row.componentStack && (
                <div>
                  <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
                    Component stack
                  </div>
                  <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {row.componentStack}
                  </pre>
                </div>
              )}
              {row.userAgent && (
                <div>
                  <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
                    Browser
                  </div>
                  <p className="font-mono break-words">{row.userAgent}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
